import { useTranslations } from 'next-intl';
import { ReactNode, useEffect, useRef } from 'react';

import { MfaVerifyChannel } from '@flipster/apis/v1/mfa/types';
import { Explain } from '@flipster/react/design-utils/explain';
import { FormattedInput } from '@flipster/react/design-utils/formatted-input';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';

interface Props {
  channel: MfaVerifyChannel;
  value?: string;
  errorMessage?: string;
  onChange: (code: string) => void;
  length?: number;
  placeholder?: string;
  disabled?: boolean;
  action?: ReactNode;
  label?: string;
  description?: string;
  autoFocus?: boolean;
}

export const OtpInput = ({
  channel,
  value,
  errorMessage,
  onChange,
  length = 6,
  placeholder,
  disabled,
  action,
  label,
  description,
  autoFocus,
}: Props) => {
  const t = useTranslations();

  const inputRef = useRef<HTMLInputElement>(null);
  const hasFocusedRef = useRef(false);

  // SMS/이메일은 코드 발송 전 disabled 상태로 마운트되어 HTML autoFocus가 걸리지 않으므로,
  // 입력이 활성화되는 시점에 한 번만 직접 focus를 준다.
  useEffect(() => {
    if (autoFocus && !disabled && !hasFocusedRef.current) {
      inputRef.current?.focus();
      hasFocusedRef.current = true;
    }
  }, [autoFocus, disabled]);

  return (
    <div className={stack({ gap: 6 })}>
      <label
        htmlFor={channel}
        className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-5', mb: 10 })}
      >
        {label}
      </label>
      <div
        className={hstack({
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'border.default.level-3',
          h: 50,
          px: 12,
          rounded: 4,
          alignContent: 'center',
        })}
      >
        <FormattedInput
          ref={inputRef}
          id={channel}
          value={value}
          className={css({
            w: '100%',
            h: '100%',
            color: 'content.default.level-5',
            _disabled: { color: 'content.default.level-1' },
            _placeholder: { color: 'content.default.level-2' },
          })}
          maxLength={length}
          formatter={(val) => val.replace(/\D/g, '')}
          onChange={onChange}
          inputMode='numeric'
          autoComplete='one-time-code'
          autoCorrect='off'
          placeholder={placeholder ?? t('Mfa_verify.r250710.Enter_code_placeholder')}
          disabled={disabled}
        />
        {action}
      </div>
      <div>
        {errorMessage && <Explain.Error css={{ textStyle: 'Text/S/Monospace/Regular' }}>{errorMessage}</Explain.Error>}
        {!errorMessage && description && (
          <Explain css={{ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-2' }}>
            {description}
          </Explain>
        )}
      </div>
    </div>
  );
};
